import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonicModule, ModalController } from '@ionic/angular';
import { MatMenuModule } from '@angular/material/menu';
import { DateTime } from 'luxon';
import { merge } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { HeaderDropdownItem, RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { ConfirmPage } from 'src/pages/confirm/confirm.component';

import { Util } from 'src/shared/util/util';
import { readSecondaryPageContext, toIsoDate } from 'src/shared/util/secondary-page-context';
import {
  SPREAD_ITEMS,
  SpreadChoice,
  paidOnLabel,
  periodLabel,
  rangeLabel,
  spreadChoiceOf,
  spreadEndDate,
  spreadEndValidator,
  spreadSummary,
} from 'src/shared/util/transaction-dates';

import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { FinanceTypesService } from 'src/shared/services/finance-types.service';
import { BankTransaction, FinancialConnectionsService } from 'src/shared/services/financial-connections.service';
import {
  FinancialTransaction,
  FinancialTransactionPermissions,
  PropertyTransactionsService,
  UpdateFinancialTransactionEntry,
  permissionsOf,
} from 'src/shared/services/property-transactions.service';
import { JOB_ICON_FALLBACK, RESERVATION_ICON, TransactionRelatedRecordsService } from 'src/shared/services/transaction-related-records.service';

import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

interface TransactionDetailParams {
  transaction?: FinancialTransaction;
  addToPnl?: { bankTransaction: BankTransaction; connectionId: number; connectionLabel?: string };
  connectionLabel?: string;
  propertyLabel?: string;
  /** "September 2026" when opened from a month's list, whose share the row carries. */
  monthLabel?: string;
}

interface ItemView {
  label: string;
  amountLabel: string;
}

const REMOVE_ACTION = 'transaction-remove-from-pnl';
const NONE_OPTION: SelectModel = { value: '', viewValue: 'None' };
const SOURCE_RECORD_KIND: Record<string, string> = { Job: 'job', GuestReservation: 'reservation' };

/**
 * One page for three jobs: editing a P&L transaction (the fields its permissions
 * allow, the reservation it belongs to, remove via the header menu), adding a
 * skipped bank row to the P&L, and showing a row TIDY wrote from a job or a stay,
 * whose fields follow that record and which may still be pointed at another stay.
 */
@Component({
  templateUrl: 'transaction-detail.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IonicModule, MatMenuModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TransactionDetailPage implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly modalCtrl = inject(ModalController);
  private readonly util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly addressesQueries = inject(AddressesQueriesService);
  private readonly financeTypes = inject(FinanceTypesService);
  private readonly financialConnections = inject(FinancialConnectionsService);
  private readonly propertyTransactions = inject(PropertyTransactionsService);
  private readonly relatedRecords = inject(TransactionRelatedRecordsService);

  readonly form = this.fb.group({
    monthly_finance_type_key: ['', Validators.required],
    address_id: ['', Validators.required],
    date: ['', Validators.required],
    spread: ['none' as SpreadChoice],
    spread_end: ['' as string | Date, spreadEndValidator],
    transacted_on: ['' as string | Date],
    description: [''],
    guest_reservation_id: [''],
  });

  readonly spreadItems = SPREAD_ITEMS;

  isRightSideContent = true;
  loaded = false;
  submitted = false;
  saving = false;
  errorMessage = '';
  mode: 'edit' | 'add-to-pnl' = 'edit';
  pageTitle = 'Transaction';
  transaction: FinancialTransaction | null = null;
  bankTransaction: BankTransaction | null = null;
  connectionId: number | null = null;
  headline = '';
  formattedAmount = '';
  dateLabel = '';
  /** "Paid Aug 29, 2026 (estimated from check-in)", shown where it says more than the date above it. */
  paidOnLine = '';
  /** "$6,268.80 of this counts in September 2026", when opened from a month that counts part of it. */
  monthShareLine = '';
  isUpcoming = false;
  sourceLabel = '';
  suggestionNote = '';
  categoryItems: SelectModel[] = [];
  propertyItems: SelectModel[] = [];
  headerMenu: HeaderDropdownItem[] = [];

  /** The row's `permissions`, per member: which fields may change, whether it can be removed or re-linked. */
  permissions: FinancialTransactionPermissions | null = null;
  /** No field of the row itself may change (a row TIDY wrote): the fields read as text. */
  readOnly = false;
  canDelete = false;
  canLinkReservation = false;
  /** The customer's own row may cover more than one day. */
  canSpread = false;
  /** The day the money moved may be corrected: a manual row, or a TIDY row whose day is a guess. */
  canCorrectPaidOn = false;
  /** How each reading counts the spread just picked. */
  spreadSummary = '';
  /** Under the Paid on field: where its day came from. */
  paidOnHelp = '';
  /** "Reset to check-in" once the customer has set the day themselves. */
  paidOnResetLabel = '';
  categoryLabel = '';
  propertyLabel = '';
  sourceRecordKind = '';
  itemViews: ItemView[] = [];
  jobId: number | null = null;
  reservationId: number | null = null;
  /** "1 hour cleaning" and "Sep 15 – Sep 23, 2026", shown beside the View links once resolved. */
  jobSummaryLabel = '';
  jobIconUrl = JOB_ICON_FALLBACK;
  reservationSummaryLabel = '';
  readonly reservationIcon = RESERVATION_ICON;

  reservationItems: SelectModel[] = [NONE_OPTION];
  /** The field shows whenever the row has or may have a reservation; it is inert when the backend locks the link. */
  showReservationSection = false;

  readonly boundSave = () => this.save();
  readonly boundSavePaidOn = () => this.savePaidOn();
  readonly boundCreateRule = () => this.createRule();

  async ngOnInit() {
    const { isRightSideContent, params } = await readSecondaryPageContext<TransactionDetailParams>(this.storage);
    this.isRightSideContent = isRightSideContent;
    if (params.addToPnl?.bankTransaction) {
      this.mode = 'add-to-pnl';
      this.bankTransaction = params.addToPnl.bankTransaction;
      this.connectionId = params.addToPnl.connectionId;
      this.pageTitle = 'Add to P&L';
      this.sourceLabel = params.addToPnl.connectionLabel || 'Bank';
    } else {
      this.mode = 'edit';
      this.transaction = params.transaction ?? null;
      this.pageTitle = 'Transaction';
      this.propertyLabel = params.propertyLabel || '';
      if (this.transaction) {
        this.readPermissions(this.transaction);
        this.sourceLabel = this.transaction.source === 'tidy'
          ? 'TIDY'
          : this.transaction.source === 'manual' ? 'Added manually' : (params.connectionLabel || 'Bank');
        this.jobId = this.relatedRecords.jobIdOf(this.transaction);
        this.reservationId = this.relatedRecords.reservationIdOf(this.transaction);
        this.showReservationSection = this.canLinkReservation || this.reservationId !== null;
      }
    }
    this.rightSidePanelService.setDialogPageTitle(this.pageTitle);
    const row = this.transaction ?? this.bankTransaction;
    if (!row) {
      this.errorMessage = 'Transaction not found.';
      this.loaded = true;
      this.cdr.markForCheck();
      return;
    }
    this.headline = row.description || 'Transaction';
    this.formattedAmount = this.propertyTransactions.formatAmount(row);
    this.dateLabel = this.transaction ? periodLabel(this.transaction) : rangeLabel(this.bankTransaction.date, null);
    const firstDay = this.transaction?.period_start_date ?? this.bankTransaction?.date;
    this.isUpcoming = !!firstDay && firstDay > DateTime.local().toFormat('yyyy-MM-dd');
    this.monthShareLine = this.buildMonthShareLine(params.monthLabel);
    this.describePayment();
    if (this.canDelete) {
      this.setupHeaderMenu();
    }
    await this.loadPickers();
    if (this.readOnly) {
      this.describeReadOnlyRow();
    } else {
      this.prefillForm();
      this.applyPermissions();
    }
    this.prefillPaidOn();
    this.form.controls.guest_reservation_id.setValue(this.reservationId ? String(this.reservationId) : '', { emitEvent: false });
    this.watchFields();
    if (this.canLinkReservation) {
      await this.loadReservationOptions();
    } else {
      // Locked: the picker only shows the linked reservation, and cannot be opened.
      this.form.controls.guest_reservation_id.disable({ emitEvent: false });
      this.reservationItems = this.reservationId ? [{ value: String(this.reservationId), viewValue: `Reservation #${this.reservationId}` }] : [NONE_OPTION];
    }
    this.loaded = true;
    this.cdr.markForCheck();
    this.loadRecordSummaries();
  }

  ngOnDestroy() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.clearHeaderDropdownMenu();
    }
  }

  private readPermissions(txn: FinancialTransaction): void {
    const permissions = permissionsOf(txn);
    this.permissions = permissions;
    this.readOnly = !(permissions.monthly_finance_type || permissions.address || permissions.period_start_date || permissions.description);
    this.canDelete = permissions.delete;
    this.canLinkReservation = permissions.guest_reservation;
    this.canSpread = permissions.period_end_date;
    this.canCorrectPaidOn = permissions.transacted_on;
  }

  /**
   * The day the money moved, beside the days the row covers: the two dates accrual and
   * cash each count it by. Left out where it would only repeat a one-day row's date.
   */
  private describePayment(): void {
    const txn = this.transaction;
    if (!txn) {
      this.paidOnLine = '';
      return;
    }
    const oneDay = txn.period_end_date === txn.period_start_date;
    const repeatsDate = oneDay && txn.transacted_on === txn.period_start_date && txn.transacted_at_origin !== 'customer';
    this.paidOnLine = repeatsDate ? '' : paidOnLabel(txn);
    const isStay = txn.source_record_type === 'GuestReservation';
    if (txn.transacted_at_origin === 'customer') {
      this.paidOnHelp = 'You set this day. Cash counts the whole amount in its month.';
      this.paidOnResetLabel = isStay ? 'Reset to check-in' : 'Reset to the date';
    } else {
      this.paidOnHelp = isStay
        ? 'TIDY estimated this from check-in. If the money came on another day, set it here. Cash counts the whole amount in its month.'
        : 'The date, unless you set another day. Cash counts the whole amount in its month.';
      this.paidOnResetLabel = '';
    }
  }

  private buildMonthShareLine(monthLabel: string | undefined): string {
    const share = this.transaction?.month_share_amount;
    if (!monthLabel || typeof share !== 'number' || share === this.transaction.amount) { return ''; }
    return `${this.formatCents(share)} of this counts in ${monthLabel}`;
  }

  /** Names the job and the reservation after the page paints; the links work without them. */
  private async loadRecordSummaries(): Promise<void> {
    const addressId = this.transaction?.address_id;
    if (!addressId) { return; }
    const [job, reservation] = await Promise.all([
      this.jobId ? this.relatedRecords.jobSummary(addressId, this.jobId, this.transaction.period_start_date).catch(() => null) : null,
      this.reservationId ? this.relatedRecords.reservationSummary(addressId, this.reservationId).catch(() => null) : null,
    ]);
    this.jobSummaryLabel = job?.service ?? '';
    this.jobIconUrl = job?.iconUrl ?? JOB_ICON_FALLBACK;
    this.reservationSummaryLabel = reservation?.dateLabel ?? (this.reservationId ? `Reservation #${this.reservationId}` : 'No reservation');
    if (!this.canLinkReservation && this.reservationId && reservation) {
      this.reservationItems = [{ value: String(this.reservationId), viewValue: reservation.label }];
    }
    this.cdr.markForCheck();
  }

  /** "Remove from P&L" lives in the header three-dot menu: the dialog header on desktop, the top nav on mobile. */
  private setupHeaderMenu(): void {
    if (this.mode !== 'edit') { return; }
    this.headerMenu = [{ label: 'Remove from P&L', action: REMOVE_ACTION }];
    if (this.isRightSideContent) {
      this.rightSidePanelService.setHeaderDropdownMenu(this.headerMenu);
    }
    this.rightSidePanelService.dropDownMenuAction$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(action => {
        if (action === REMOVE_ACTION) {
          this.confirmDelete();
        }
      });
  }

  private async loadPickers(): Promise<void> {
    const [categories, addresses] = await Promise.all([
      this.financeTypes.getSelectItems().catch(() => [] as SelectModel[]),
      this.addressesQueries.getAddressesOnce().catch(() => []),
    ]);
    this.categoryItems = categories;
    this.propertyItems = addresses.map(address => ({
      value: String(address.id),
      viewValue: address['address_name'] || address.address1 || `Property #${address.id}`,
    }));
  }

  private describeReadOnlyRow(): void {
    const txn = this.transaction;
    this.categoryLabel = txn.monthly_finance_type?.label
      || this.categoryItems.find(item => item.value === txn.monthly_finance_type?.key)?.viewValue
      || '';
    this.propertyLabel = this.propertyLabel
      || this.propertyItems.find(item => item.value === String(txn.address_id))?.viewValue
      || '';
    this.sourceRecordKind = SOURCE_RECORD_KIND[txn.source_record_type ?? ''] ?? 'record';
    const items = txn.items ?? [];
    // A single part is the row itself; only merged charges are worth itemizing.
    this.itemViews = items.length > 1
      ? items.map(item => ({ label: item.label, amountLabel: this.formatCents(item.amount) }))
      : [];
  }

  private prefillForm(): void {
    if (this.mode === 'add-to-pnl') {
      this.form.patchValue({
        date: this.bankTransaction.date,
        description: this.bankTransaction.description || '',
        address_id: this.propertyItems.length === 1 ? this.propertyItems[0].value : '',
      });
      return;
    }
    const txn = this.transaction;
    const suggestedKey = txn.monthly_finance_type?.key ? '' : txn.suggestion?.monthly_finance_type_key ?? '';
    if (suggestedKey) {
      const label = this.categoryItems.find(item => item.value === suggestedKey)?.viewValue ?? suggestedKey;
      const confidence = txn.suggestion?.confidence ? ` (${txn.suggestion.confidence} confidence)` : '';
      this.suggestionNote = `TIDY AI was not sure about this one and suggests "${label}"${confidence}. Save to use it, or pick another category.`;
    }
    const spread = spreadChoiceOf(txn.period_start_date, txn.period_end_date);
    this.form.patchValue({
      monthly_finance_type_key: txn.monthly_finance_type?.key ?? suggestedKey,
      address_id: txn.address_id ? String(txn.address_id) : (this.propertyItems.length === 1 ? this.propertyItems[0].value : ''),
      date: txn.period_start_date,
      spread,
      spread_end: spread === 'custom' ? txn.period_end_date : '',
      description: txn.description || '',
    }, { emitEvent: false });
    this.spreadSummary = spreadSummary(txn.period_start_date, txn.period_end_date);
  }

  /** Pristine until the customer picks a day, so a save that never touched it leaves TIDY's estimate alone. */
  private prefillPaidOn(): void {
    this.form.controls.transacted_on.setValue(this.transaction?.transacted_on ?? '', { emitEvent: false });
    this.form.controls.transacted_on.markAsPristine();
  }

  /** A field this member may not change stays visible but disabled, and is left out of the save. */
  private applyPermissions(): void {
    const permissions = this.permissions;
    if (!permissions) { return; }
    if (!permissions.monthly_finance_type) { this.form.controls.monthly_finance_type_key.disable({ emitEvent: false }); }
    if (!permissions.address) { this.form.controls.address_id.disable({ emitEvent: false }); }
    if (!permissions.period_start_date) { this.form.controls.date.disable({ emitEvent: false }); }
    if (!permissions.description) { this.form.controls.description.disable({ emitEvent: false }); }
  }

  private watchFields(): void {
    const { date, spread, spread_end, transacted_on } = this.form.controls;
    merge(date.valueChanges, spread.valueChanges, spread_end.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        spread_end.updateValueAndValidity({ emitEvent: false });
        const start = toIsoDate(date.value);
        this.spreadSummary = start ? spreadSummary(start, spreadEndDate(start, spread.value, toIsoDate(spread_end.value))) : '';
        this.cdr.markForCheck();
      });
    // A guessed paid day is the row's first day, and the backend moves it with the date.
    date.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (this.transaction?.transacted_at_origin !== 'guess' || transacted_on.dirty) { return; }
        transacted_on.setValue(toIsoDate(value) ?? '', { emitEvent: false });
      });
    this.form.controls.address_id.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // A stay must belong to the row's property, so a new property means a new list and no stale pick.
        this.form.controls.guest_reservation_id.setValue('', { emitEvent: false });
        if (this.canLinkReservation) { this.loadReservationOptions(); }
      });
  }

  /** Reservations of the row's property. The linked one stays selectable even when outside the fetched page. */
  private async loadReservationOptions(): Promise<void> {
    const addressId = this.readOnly ? this.transaction?.address_id : Number(this.form.controls.address_id.value);
    if (!addressId) {
      this.reservationItems = [NONE_OPTION];
      this.cdr.markForCheck();
      return;
    }
    const reservations = await this.relatedRecords.reservationOptions(addressId).catch(() => [] as SelectModel[]);
    const current = this.form.controls.guest_reservation_id.value;
    const withCurrent = !current || reservations.some(item => item.value === current)
      ? reservations
      : [{ value: current, viewValue: `Reservation #${current}` }, ...reservations];
    this.reservationItems = [NONE_OPTION, ...withCurrent];
    this.cdr.markForCheck();
  }

  async save(): Promise<void> {
    this.submitted = true;
    if (this.readOnly || this.form.invalid || this.saving) { return; }
    if (this.demoBlock.block()) { return; }
    const value = this.form.getRawValue();
    this.saving = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    try {
      if (this.mode === 'add-to-pnl') {
        await this.financialConnections.addToProfitAndLoss(this.connectionId, this.bankTransaction.id, {
          monthly_finance_type_key: value.monthly_finance_type_key,
          address_id: Number(value.address_id),
          period_start_date: toIsoDate(value.date),
        });
        this.util.showSuccess('Added to your P&L');
      } else {
        const entry = this.editableEntry(value);
        if (Object.keys(entry).length > 1) {
          await this.propertyTransactions.updateFinancialTransactions([entry]);
        }
        await this.persistReservationLink(value.guest_reservation_id);
        this.util.showSuccess('Transaction saved');
      }
      await this.storage.save('didChangeTransactions', true);
      this.goBack();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Unable to save the transaction.';
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * A row TIDY wrote keeps its fields, but its reservation may still be changed, from the
   * small menu beside it. One pick is the whole action, so it saves on the spot.
   */
  async pickReservation(value: string): Promise<void> {
    if (this.saving) { return; }
    if (this.demoBlock.block()) { return; }
    const previous = this.reservationId;
    this.saving = true;
    this.errorMessage = '';
    this.form.controls.guest_reservation_id.setValue(value, { emitEvent: false });
    this.cdr.markForCheck();
    try {
      await this.persistReservationLink(value);
      await this.storage.save('didChangeTransactions', true);
      this.util.showSuccess(value ? 'Reservation saved' : 'Reservation removed');
      await this.loadRecordSummaries();
    } catch (err) {
      this.reservationId = previous;
      this.form.controls.guest_reservation_id.setValue(previous ? String(previous) : '', { emitEvent: false });
      this.errorMessage = err?.error?.message || err?.message || 'Unable to change the reservation.';
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Only the fields this member may change; the update endpoint refuses the rest. A spread
   * row sends both dates, its end worked out again from the months picked: the start alone
   * would keep the length in days, so six months moved from 3 March to 3 April would end a
   * day late. The paid day goes only when the customer picked one, or saving would turn
   * TIDY's estimate into their own day.
   */
  private editableEntry(value: ReturnType<typeof this.form.getRawValue>): UpdateFinancialTransactionEntry {
    const permissions = this.permissions;
    const entry: UpdateFinancialTransactionEntry = { id: this.transaction.id };
    const start = toIsoDate(value.date);
    if (permissions?.monthly_finance_type) { entry.monthly_finance_type_key = value.monthly_finance_type_key; }
    if (permissions?.address) { entry.address_id = Number(value.address_id); }
    if (permissions?.period_start_date) { entry.period_start_date = start; }
    if (permissions?.period_end_date) {
      const end = spreadEndDate(start, value.spread, toIsoDate(value.spread_end));
      if (end) { entry.period_end_date = end; }
    }
    const paidOn = this.pickedPaidOn();
    if (permissions?.transacted_on && paidOn) { entry.transacted_on = paidOn; }
    if (permissions?.description) { entry.description = value.description ?? ''; }
    return entry;
  }

  /**
   * The day the customer picked in Paid on, or null when they left it alone. Picking the
   * day TIDY estimated still counts: sent, it stays theirs when the date moves.
   */
  private pickedPaidOn(): string | null {
    const control = this.form.controls.transacted_on;
    const picked = toIsoDate(control.value);
    if (!control.dirty || !picked) { return null; }
    const alreadyTheirs = picked === this.transaction?.transacted_on && this.transaction?.transacted_at_origin === 'customer';
    return alreadyTheirs ? null : picked;
  }

  /**
   * A TIDY row takes one change, its paid day, and nothing else in the entry, so it saves
   * on its own and the page stays open, like the reservation picker beside it.
   */
  async savePaidOn(): Promise<void> {
    if (!this.canCorrectPaidOn || this.saving) { return; }
    const paidOn = this.pickedPaidOn();
    if (!paidOn) {
      this.util.showSuccess('Nothing to save');
      return;
    }
    await this.writePaidOn(paidOn, 'Paid date saved');
  }

  /** Gives the day back to TIDY's estimate: check-in on a stay, the first day on the customer's own row. */
  async resetPaidOn(): Promise<void> {
    if (!this.canCorrectPaidOn || this.saving) { return; }
    await this.writePaidOn(null, 'Paid date reset');
  }

  private async writePaidOn(paidOn: string | null, successMessage: string): Promise<void> {
    if (this.demoBlock.block()) { return; }
    this.saving = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    try {
      const [updated] = await this.propertyTransactions.updateFinancialTransactions([{ id: this.transaction.id, transacted_on: paidOn }]);
      if (updated) {
        this.transaction = { ...this.transaction, ...updated };
        this.readPermissions(this.transaction);
      }
      this.describePayment();
      this.prefillPaidOn();
      await this.storage.save('didChangeTransactions', true);
      this.util.showSuccess(successMessage);
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Unable to save the paid date.';
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  /** Sent only when the pick differs from the saved link. On a TIDY job row the backend moves the whole booking. */
  private async persistReservationLink(value: string | null): Promise<void> {
    if (!this.canLinkReservation) { return; }
    const next = value ? Number(value) : null;
    if (next === (this.reservationId ?? null)) { return; }
    const link = await this.propertyTransactions.setGuestReservationLink({
      record_type: 'FinancialTransaction',
      record_id: this.transaction.id,
      guest_reservation_id: next,
    });
    this.reservationId = link?.guest_reservation_id ?? next;
  }

  async openJob(): Promise<void> {
    if (!this.jobId) { return; }
    try {
      await this.relatedRecords.openJob(this.jobId, this.transaction?.address_id ?? null);
    } catch (err) {
      this.util.showError(err?.error?.message || err?.message || 'Unable to open the job.');
    }
  }

  async openReservation(): Promise<void> {
    const picked = Number(this.form.controls.guest_reservation_id.value) || null;
    const reservationId = picked ?? this.reservationId;
    if (!reservationId) { return; }
    try {
      await this.relatedRecords.openReservation(reservationId, this.transaction?.address_id ?? null, this.propertyLabel);
    } catch (err) {
      this.util.showError(err?.error?.message || err?.message || 'Unable to open the reservation.');
    }
  }

  async confirmDelete(): Promise<void> {
    if (!this.transaction || !this.canDelete) { return; }
    if (this.demoBlock.block()) { return; }
    const isFromBank = this.transaction.source === 'bank';
    const modal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: {
        title: 'Remove from P&L',
        body: isFromBank
          ? 'This removes the transaction from your P&L. The bank row stays, and you can add it back later from the bank transactions list.'
          : 'This removes the transaction from your P&L.',
        backText: 'Cancel',
        confirmText: 'Remove',
        confirmAction: async () => {
          try {
            await this.propertyTransactions.deleteFinancialTransaction(this.transaction.id);
            await this.storage.save('didChangeTransactions', true);
            modal.dismiss();
            this.util.showSuccess('Removed from your P&L');
            this.goBack();
          } catch (err) {
            modal.dismiss();
            // Backend authorizes against the row's property; a row with none yet cannot be removed.
            const isUnassignedRow = !this.transaction.address_id && err?.status === 403;
            this.util.showError(isUnassignedRow
              ? 'This transaction has no property yet, so it cannot be removed on its own. Set the property first.'
              : err?.error?.message || err?.message || 'Unable to remove the transaction.');
          }
        },
        confirmButtonClass: 'primary',
      },
      animated: false,
      cssClass: 'confirm-modal',
    });
    modal.present();
  }

  createRule(): void {
    const row = this.transaction ?? this.bankTransaction;
    if (!row) { return; }
    const value = this.form.getRawValue();
    this.rightSidePanelService.navigateTo('transaction-rule-form', {
      rule: null,
      sample: { description: row.description, transaction_type: row.transaction_type },
      suggestedApplies: {
        classification: 'pnl',
        monthly_finance_type_key: value.monthly_finance_type_key || undefined,
        address_id: value.address_id ? Number(value.address_id) : undefined,
      },
    });
  }

  goBack(): void {
    if (this.isRightSideContent) {
      if (this.rightSidePanelService.historyContext$.getValue().length > 1) {
        this.rightSidePanelService.goBack();
      } else {
        this.rightSidePanelService.closeRightSidePanel();
      }
    } else {
      this.navCtrl.back();
    }
  }

  private formatCents(cents: number): string {
    return (Math.abs(cents) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
}
